import { Animator, animate, View } from '@play-co/timestep-core/ui';
import SceneManager from '../logic/SceneManager';
import StateObserver from '../../StateObserver';
import { getScreenTop, waitForItPromise, withLoading } from '../../lib/utils';
import { startSceneTransition } from '../../state/ui';
import { EntityPosition, getScenePosition } from './position.config';
import { ViewPosition } from '@play-co/timestep-core/lib/ui/View';
import { Entity } from '../logic/EntityManager';
import { Position } from './PositionModel';
import { toPromise } from 'src/lib/animation';
import { getMutex } from 'src/replicant/utils/mutex';
import statePromise from 'src/lib/statePromise';
import { Feature } from 'src/replicant/analytics';

export class PositionController<V extends Entity = Entity> {
  public get position() {
    return this._position;
  }
  protected set position(position: Position | null) {
    this._position = position;
  }
  protected _position: Position | null = null;

  protected animate: Animator;
  private movementMutex = getMutex();

  constructor(
    protected sceneManager: SceneManager,
    protected view: V,
  ) {
    this.animate = animate(this.view);
  }

  public init(position: Position) {
    if (!position) return;

    this.position = position;

    if (!this.sceneManager.isValidScene(position?.scene)) return;

    const scene = this.sceneManager.getScene(position?.scene);
    const sceneView = scene?.getView();
    const destination = getScenePosition(position);

    if (!sceneView || !destination) return;

    this.view.updateOpts({
      superview: sceneView,
      ...destination,
      zIndex: destination.y,
    });
    this.view.style.y -= getScreenTop();
  }

  public async moveTo(position: Position | null, duration?: number) {
    try {
      this.position = position;
      await toPromise(this.animate.clear());
      this.view.moving = true;
      await this.movementMutex.lock();

      if (!position) return;

      const sceneId = position.scene;
      if (!this.sceneManager.isValidScene(sceneId)) return;

      const scene = this.sceneManager.getScene(sceneId)!;
      const sceneView = scene.getView();

      // Wait for scene assets to load.
      await withLoading(() => scene.preload(), {
        feature: Feature.SCENE_MANAGER._,
        subFeature: Feature.SCENE_MANAGER.PRELOAD,
      });

      if (!sceneView) {
        await toPromise(
          this.animate.then({ opacity: 0 }, 500).then(() => {
            this.view.removeFromSuperview();
            this.view.style.opacity = 1;
          }),
        );

        return;
      }

      const isSceneTransition = sceneView !== this.view.getSuperview();
      if (isSceneTransition) {
        StateObserver.dispatch(startSceneTransition(position.scene));
      }

      const destination = getScenePosition(position);

      if (!destination) return;

      if (duration == null) {
        duration = this.getTransitionDuration(sceneView, destination);
      }

      if (!isSceneTransition) {
        this.view.setFlipped(destination!.x > this.view.style.x);
      }
      this.prepareForTransition(sceneView);
      await this.animateViewPosition(sceneView, destination, duration);

      if (isSceneTransition) {
        await statePromise((state) => state.ui.transition.phase === 'entered');
      }
    } finally {
      this.view.moving = false;
      this.movementMutex.release();
    }
  }

  public getSceneLocation() {
    if (
      this.position &&
      ['livingRoom', 'friendsLivingRoom'].includes(this.position.scene)
    ) {
      return this.position.location;
    }

    return 'c';
  }

  public async waitForMovement() {
    await this.movementMutex.lock();
    this.movementMutex.release();
  }

  protected prepareForTransition(sceneView: View) {
    this.view.updateOpts({ superview: sceneView });
  }

  protected getTransitionDuration(
    sceneView: View,
    destination: EntityPosition,
  ) {
    if (sceneView !== this.view.getSuperview()) {
      return 0;
    }

    const { x, y } = this.view.style;
    const distanceX = Math.abs(x - destination.x);
    const distanceY = Math.abs(y - destination.y);
    const distance = Math.sqrt(Math.pow(distanceX, 2) + Math.pow(distanceY, 2));
    return 4 * distance;
  }

  protected async animateViewPosition(
    sceneView: View,
    destination: Partial<ViewPosition & { zIndex: number }> & {
      x: number;
      y: number;
    },
    duration: number,
  ) {
    await toPromise(
      this.animate
        .clear()
        .then(
          {
            ...destination,
            scale: destination.scale ?? 1,
            zIndex: destination.zIndex ?? destination.y,
          },
          duration,
          animate.easeInOutSine,
        )
        .then(() => {
          if (this.view.getSuperview() !== sceneView) {
            this.view.updateOpts({ superview: sceneView });
            this.view.style.y -= getScreenTop();
          }
          this.view.setFlipped(false);
        }),
    );
  }

  public destroy() {
    this.view.removeFromSuperview();
  }
}
